import { ref, computed } from "vue"
import {
  Configuration,
  AccountInfo,
  PublicClientApplication,
  AuthenticationResult,
} from "@azure/msal-browser"
import { IUserAuth } from "@/models/userAuth/userAuth.types"
import { UserAuthParser } from "@/models/userAuth/userAuth.parser"
import { apiClient } from "@/api/msGraph/client"
import { apiGatewayClient } from "@/api/apiGateway/client"
import { useMockPermissionsStore } from "@/stores/mockPermissions"
import { Role, ROLES } from "./PermissionsService"
import { datadogRum } from "@datadog/browser-rum"
import dayjs from "dayjs"

const redirectUrl = () => {
  const env = __APP_VERCEL_ENV__
  const url = __APP_VERCEL_URL__
  const branch = __APP_VERCEL_BRANCH__
  const branches = ["staging"]

  if (!url || env === "production" || branches.includes(branch)) {
    return import.meta.env.SO_NOVA_URL
  } else {
    return "https://" + url
  }
}

const msalConfig: Configuration = {
  auth: {
    clientId: "03379269-263e-4eb7-9022-81384e9fef69",
    authority:
      "https://login.microsoftonline.com/2fd125f7-d450-4fdb-986d-a48296918cbb",
    redirectUri: redirectUrl(),
    postLogoutRedirectUri: "/",
  },
  cache: {
    cacheLocation: "localStorage",
  },
}

const loginRequest = {
  scopes: ["User.Read"],
}

export interface IAuthServiceConfig {
  usePopupApi?: boolean
}

export interface IAuthService {
  init(config?: IAuthServiceConfig): void
  ensureAuthenticated(): Promise<boolean>
  getIsAuthenticated(): boolean
  getIsAuthenticating(): boolean
  getToken(): Promise<IToken>
  getCurrentUser(): IUserAuth
  getUserRoles(): Role[]
  getIsPopupApi(): boolean
  isDeveloper(): boolean
  logout(): Promise<void>
}

interface IToken {
  accessToken: string
  idToken: string
}

class AuthServiceClass implements IAuthService {
  private static instance: AuthServiceClass | null = null
  private msalInstance = new PublicClientApplication(msalConfig)
  private authenticationPromise: Promise<void> | null = null
  private accounts = [] as AccountInfo[]
  private isPopupApi = false
  private readonly isAuthenticated = ref(false)
  private readonly token: IToken = { accessToken: "", idToken: "" }
  private readonly isAuthenticating = ref(false)
  private readonly activeAccount = ref<IUserAuth>({
    environment: "",
    homeAccountId: "",
    localAccountId: "",
    tenantId: "",
    username: "",
    name: "",
    roles: [],
  })

  public static getInstance() {
    if (!this.instance) {
      this.instance = new AuthServiceClass()
    }
    return this.instance
  }

  async init(config?: IAuthServiceConfig) {
    if (config?.usePopupApi) {
      this.isPopupApi = true
    }

    await this.authenticate()
  }

  async ensureAuthenticated() {
    await this.authenticate()
    return this.isAuthenticated.value
  }

  getIsAuthenticated() {
    return computed(() => this.isAuthenticated.value).value
  }

  getIsAuthenticating() {
    return computed(() => this.isAuthenticating.value).value
  }

  async getToken({ forceRefresh } = { forceRefresh: false }) {
    await this.authenticate(forceRefresh)
    return this.token
  }

  getCurrentUser() {
    return computed(() => this.activeAccount.value).value
  }

  getUserRoles() {
    if (useMockPermissionsStore().enabled) {
      return useMockPermissionsStore().roles
    } else {
      return this.getCurrentUser().roles ?? ([] as Role[])
    }
  }

  isDeveloper() {
    return !!this.getCurrentUser().roles.includes(ROLES.DEVELOPER)
  }

  getIsPopupApi() {
    return this.isPopupApi
  }

  async logout() {
    datadogRum.clearUser()

    if (this.getIsPopupApi()) {
      await this.msalInstance.logoutPopup({
        postLogoutRedirectUri: redirectUrl(),
        mainWindowRedirectUri: redirectUrl(),
      })
    } else {
      await this.msalInstance.logoutRedirect()
    }
  }

  private async authenticate(forceRefresh = false) {
    if (this.authenticationPromise) {
      return this.authenticationPromise
    }

    this.authenticationPromise = this.runAuthentication(forceRefresh)

    try {
      await this.authenticationPromise
    } finally {
      this.authenticationPromise = null
    }
  }

  private async runAuthentication(forceRefresh: boolean) {
    this.isAuthenticating.value = true

    try {
      await this.handleRedirectPromise()
      this.setAccounts()

      if (this.accounts.length === 1) {
        this.setActiveAccount(this.accounts[0])
      }

      const activeAccount = this.getActiveAccount()

      if (this.accounts.length === 0) {
        await this.silentSSO()
      } else if (activeAccount) {
        await this.acquireTokenSilently(activeAccount, forceRefresh)
      } else {
        await this.handleInteractiveLogin()
      }
    } catch (error) {
      console.error("Authentication failed:", error)
      this.setIsAuthenticated(false)
    } finally {
      this.isAuthenticating.value = false
    }
  }

  private async silentSSO() {
    try {
      const response = await this.msalInstance.ssoSilent(loginRequest)
      this.handleAuthentication(response)
    } catch (error) {
      console.error("Silent SSO failed.", error)
      this.handleInteractiveLogin()
    }
  }

  private async acquireTokenSilently(
    account: AccountInfo,
    forceRefresh = false,
  ) {
    try {
      const response = await this.msalInstance.acquireTokenSilent({
        forceRefresh: forceRefresh || this.checkForExpiredTokens(),
        account,
        ...loginRequest,
      })
      this.handleAuthentication(response)
    } catch (error) {
      console.error("Silent Token Acquisition failed.", error)
      this.handleInteractiveLogin()
    }
  }

  private async handleInteractiveLogin() {
    if (this.getIsPopupApi()) {
      this.loginPopup()
    } else {
      this.loginRedirect()
    }
  }

  private async loginPopup() {
    try {
      const response = await this.msalInstance.loginPopup({
        ...loginRequest,
        redirectUri: redirectUrl(),
      })
      this.handleAuthentication(response)
    } catch (error) {
      console.error("Failed to login", error)
    }
  }

  private async loginRedirect() {
    try {
      await this.msalInstance.loginRedirect(loginRequest)
      await this.handleRedirectPromise()
    } catch (error) {
      console.error("Failed to login", error)
    }
  }

  private checkForExpiredTokens() {
    const activeAccount = this.getActiveAccount()

    // More info about the token expiration issue: https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/4206
    const now = dayjs()
    const idTokenExp = activeAccount?.idTokenClaims?.exp ?? 0
    const tokenExpDate = dayjs(idTokenExp * 1000)
    return tokenExpDate.subtract(5, "minutes").isBefore(now)
  }

  private handleAuthentication(authResult: AuthenticationResult) {
    if (authResult.account) {
      this.setActiveAccount(authResult.account)
      this.setIsAuthenticated(true)
      this.setTokens(authResult.accessToken, authResult.idToken)
      this.setApiClientsAuthorizationHeader()
    } else {
      this.setIsAuthenticated(false)
    }
  }

  private async handleRedirectPromise() {
    const response = await this.msalInstance.handleRedirectPromise()

    if (response) {
      this.handleAuthentication(response)
    }
  }

  private setActiveAccount(account: AccountInfo) {
    this.msalInstance.setActiveAccount(account)
    this.setDatadogUser(account)
    this.activeAccount.value = UserAuthParser.toDomain(account)
  }

  private setAccounts() {
    this.accounts = this.msalInstance.getAllAccounts()
  }

  private getActiveAccount() {
    return this.msalInstance.getActiveAccount()
  }

  private setIsAuthenticated(val: boolean) {
    this.isAuthenticated.value = val
  }

  private setTokens(accessToken: string, idToken: string) {
    this.token.accessToken = accessToken
    this.token.idToken = idToken
  }

  private setApiClientsAuthorizationHeader() {
    apiClient.defaults.headers.common = {
      ...apiClient.defaults.headers.common,
      Authorization: `Bearer ${this.token.accessToken}`,
    }
    apiGatewayClient.defaults.headers.common = {
      ...apiGatewayClient.defaults.headers.common,
      Authorization: `Bearer ${this.token.accessToken}`,
    }
  }

  private setDatadogUser(account: AccountInfo) {
    datadogRum.setUser({
      id: account.localAccountId,
      email: account.username,
      name: account.name ?? "Anonymous",
      roles: account.idTokenClaims?.roles?.join(","),
    })
  }
}

export const AuthService = AuthServiceClass.getInstance()
